import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const features = [
  {
    title: '一對一教學',
    desc: '完全依照你的狀態調整課程內容與節奏，不趕進度，不比較他人。',
    img: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/b3012e9e-efc4-4686-8eeb-f882b64b75bf_compressed_G_Image_u5ood0u5ood0u5oo.webp',
    tag: '完全量身訂做',
  },
  {
    title: '小班制課程',
    desc: '2–4 人的小班環境，保有共學樂趣，同時維持足夠的個人關注與學習空間。',
    img: 'https://readdy.ai/api/search-image?query=small%20group%20guzheng%20music%20class%20with%20two%20to%20three%20students%2C%20warm%20studio%20setting%20with%20wooden%20floors%2C%20natural%20daylight%2C%20calm%20and%20supportive%20atmosphere%2C%20muted%20earthy%20tones%2C%20minimalist%20Japanese%20aesthetic&width=600&height=400&seq=feat002&orientation=landscape',
    tag: '溫暖共學',
  },
  {
    title: '節奏可彈性調整',
    desc: '生活有起伏是正常的。課程頻率與學習強度，都可以隨著你的生活狀態一起調整。',
    img: 'https://readdy.ai/api/search-image?query=hands%20gently%20playing%20guzheng%20silk%20strings%2C%20close-up%20detail%20shot%2C%20warm%20natural%20light%2C%20wooden%20grain%20texture%2C%20serene%20meditative%20mood%2C%20muted%20beige%20and%20warm%20brown%20tones%2C%20macro%20photography&width=600&height=400&seq=feat003&orientation=landscape',
    tag: '彈性課表',
  },
];

export default function TeachingFeaturesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#f2ede5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Teaching</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-serif-tc text-[#2c2420] leading-[1.4]"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
            >
              教學方式<br />
              <span className="text-[#9a8470]">以你的節奏為準</span>
            </h2>
            <Link
              to="/teaching"
              className="inline-flex items-center gap-2 text-[1rem] font-medium text-[#7a6248] hover:text-[#5a4a3a] transition-colors cursor-pointer whitespace-nowrap"
            >
              了解完整教學方式
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left large card */}
          <div
            className={`lg:row-span-2 rounded-2xl overflow-hidden relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.1s', minHeight: '480px' }}
          >
            <img
              src={features[0].img}
              alt={features[0].title}
              className="w-full h-full object-cover object-top absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1410]/80 via-transparent to-transparent"></div>
            <div className="absolute top-5 left-5">
              <span className="px-3 py-1.5 rounded-full bg-white/85 text-[#3a2e22] text-[0.8125rem] font-medium">
                {features[0].tag}
              </span>
            </div>
            <div className="absolute bottom-7 left-7 right-7">
              <h3 className="font-serif-tc text-white mb-3" style={{ fontSize: '1.375rem' }}>
                {features[0].title}
              </h3>
              <p className="text-white/75 text-[1rem] leading-[1.75]">
                {features[0].desc}
              </p>
            </div>
          </div>

          {/* Right top card */}
          <div
            className={`lg:col-span-2 rounded-2xl overflow-hidden relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.2s', minHeight: '220px' }}
          >
            <img
              src={features[1].img}
              alt={features[1].title}
              className="w-full h-full object-cover object-top absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1410]/70 to-transparent"></div>
            <div className="absolute inset-0 flex items-end p-8">
              <div>
                <span className="inline-block mb-3 px-3 py-1.5 rounded-full bg-[#4a6741]/80 text-white text-[0.8125rem] font-medium">
                  {features[1].tag}
                </span>
                <h3 className="font-serif-tc text-white mb-2" style={{ fontSize: '1.375rem' }}>
                  {features[1].title}
                </h3>
                <p className="text-white/75 text-[1rem] leading-[1.75] max-w-sm">
                  {features[1].desc}
                </p>
              </div>
            </div>
          </div>

          {/* Right bottom card */}
          <div
            className={`lg:col-span-2 rounded-2xl overflow-hidden relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.3s', minHeight: '220px' }}
          >
            <img
              src={features[2].img}
              alt={features[2].title}
              className="w-full h-full object-cover object-top absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#2c2420]/75 to-transparent"></div>
            <div className="absolute inset-0 flex items-end p-8">
              <div>
                <span className="inline-block mb-3 px-3 py-1.5 rounded-full bg-[#b8a07a]/80 text-white text-[0.8125rem] font-medium">
                  {features[2].tag}
                </span>
                <h3 className="font-serif-tc text-white mb-2" style={{ fontSize: '1.375rem' }}>
                  {features[2].title}
                </h3>
                <p className="text-white/75 text-[1rem] leading-[1.75] max-w-sm">
                  {features[2].desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
